/**
 * Personalization configuration related i18n text - English
 */
export default {
    title: 'Personalization Configuration',
    readonlyNotice:
        'Your account is a regular user and can only view configurations. Contact the tenant admin to make changes.',
    tabs: {
        portal: 'Portal Enterprise Showcase',
        cargowareX: 'CargoWare X System UI'
    },
    cargowareX: {
        description: 'Configure CargoWare X system branding and Control Tower assistant',
        systemUi: {
            title: 'System UI Configuration',
            logoLabel: 'System Logo',
            logoSubLabel: '(shown in left sidebar)',
            logoTip: 'Shown in the left sidebar; recommended size 200×60 px',
            logoUpload: 'Click to upload Logo',
            logoSizeHint: 'Recommended ratio: 10:3',
            iconLabel: 'System Icon',
            iconSubLabel: '(browser tab icon)',
            iconTip: 'Browser tab icon; recommended size 64×64 px (1:1)',
            iconUpload: 'Click to upload icon',
            iconSizeHint: 'Recommended ratio: 1:1',
            titleLabel: 'System Title',
            titleSubLabel: '(browser tab and system header)',
            titlePlaceholder: 'Enter system title (maximum 100 characters)',
            titleTip: 'Shown in the browser tab and system header',
            replace: 'Replace',
            crop: 'Crop',
            remove: 'Delete',
            cropLogoTitle: 'Crop System Logo',
            cropIconTitle: 'Crop System Icon',
            cropAreaAria: 'Adjustable crop area',
            cropSize: 'Current crop size: {w} × {h} px',
            cropLogoRatio: 'Recommended ratio: 10:3',
            cropIconRatio: 'Recommended ratio: 1:1',
            cropTip: 'Drag the corners to adjust the crop area. Only the cropped result will be saved.',
            cropSuccess: 'Crop result saved'
        },
        assistant: {
            title: 'Control Tower Assistant',
            switchLabel: 'Show Control Tower Assistant',
            tip: 'Controls the visibility of the Control Tower Assistant entry in the system header'
        },
        messages: {
            saveSuccess: 'System UI configuration saved',
            saveFail: 'Failed to save system UI configuration',
            saveNeedPortalConfig: 'Please save website basic info first',
            versionMissing: 'Version is missing, please refresh and try again',
            uploadSuccess: 'Upload successful',
            uploadFail: 'Image upload failed',
            uploadTooLarge: 'Image size cannot exceed 5MB',
            uploadNotImage: 'Please upload an image file',
            uploadDimensionMismatch:
                'Image dimensions do not match. Please crop to the correct size and re-upload'
        },
        help: {
            systemLogo: 'Affects the Logo displayed in the CargoWare X left sidebar.',
            systemIcon: 'Affects the icon displayed in the CargoWare X browser tab.',
            systemTitle: 'Affects the CargoWare X browser tab and header title.',
            browser: 'Affects the page title and favicon in the browser tab.',
            browserTitle: 'Affects the page title shown in the portal browser tab.',
            browserIcon: 'Affects the favicon shown in the portal browser tab.',
            header: 'Affects the enterprise Logo shown at the top of the portal homepage.',
            slogan: 'Affects the company slogan and description on the portal hero.',
            previewSystemTitle: 'Home - CargoWare X',
            previewPortalTitle: 'Enterprise Portal',
            navHome: 'Home',
            navBpm: 'BPM Center',
            navBi: 'Analytics',
            navOrder: 'Consignment Order',
            breadcrumb: 'Home / System Settings / Personalization',
            portalNav: 'Advantages · Services & Network · Company News',
            portalSlogan:
                'CargoWare X is dedicated to providing reliable and efficient integrated logistics services for global customers.',
            aiTitle: 'Tenant Portal AI Assistant',
            aiStatus: 'Online to serve you',
            aiGreeting: 'Hello! CargoWare X AI assistant at your service.',
            aiQuickTitle: 'Quick Inquiry',
            aiQuick1: '› How much for FCL Shenzhen to Hamburg',
            aiQuick2: '› How do new customers start working with us?',
            aiInput: 'Enter your message'
        }
    },
    portalSection: {
        title: 'Portal Enterprise Showcase',
        description: 'Configure portal content, branding and page modules'
    },
    upgrade: {
        title: 'Upgrade to unlock full branding capabilities',
        description:
            'You are on the standard portal and can only configure the website skin and system-assigned domain. Upgrade to CargoWare X to unlock custom branding, AI and more portal features.',
        button: 'Upgrade to CargoWare X'
    },
    generatedSource: {
        prefix: 'Content auto-generated from',
        headerLogo: 'Enterprise Profile > Basic Info > Logo',
        slogan: 'Enterprise Profile > About Us > Description'
    },
    headerModule: {
        logoTitle: 'Header Logo',
        sloganTitle: 'Company Slogan',
        sloganContentLabel: 'Slogan Content',
        logoPreviewAlt: 'Header Logo',
        logoPreviewEmpty: 'No Header Logo image yet. Ensure the Logo file is synced in personalization config.',
        sloganPreviewEmpty: 'No company slogan content'
    },
    portalModules: {
        defaultHint:
            'This module content comes from the enterprise profile. Please configure it there.',
        serviceHighlights: {
            title: 'Service Highlights',
            contentHint:
                "Content auto-generated from <span class='module-source-link'>Enterprise Profile &gt; Service Highlights</span>",
            empty: 'No service highlights in enterprise profile yet. Please configure them there first.',
            groupServices: 'Star Services',
            groupPorts: 'Top Ports',
            groupRoutes: 'Best Routes'
        },
        serviceNetwork: {
            title: 'Service Capabilities & Network',
            contentHint:
                "Content auto-generated from <span class='module-source-link'>Enterprise Profile &gt; Services & Coverage</span>",
            empty: 'No services & coverage data in enterprise profile yet. Please configure them there first.'
        },
        companyNews: {
            title: 'Company News',
            contentHint:
                'After enabling company news, edit content under Personalization > Company News'
        }
    },
    aiAgent: {
        title: 'AI Assistant Settings',
        enableLabel: 'Enable AI Assistant',
        enableDesc:
            'When enabled, the portal will show an AI assistant to help visitors get business information',
        greetingLabel: 'AI Greeting',
        greetingTooltip:
            'Greeting message shown when the AI assistant opens for the first time, up to 200 characters',
        greetingPlaceholder: 'Enter the greeting shown to visitors...',
        defaultGreeting:
            'Hello! I am your smart assistant. I can help with rates, routes and our services. How can I help today?'
    },
    valuesAdvantages: {
        title: 'Core Values & Advantages',
        description:
            'Configure core service advantage cards shown on the portal. Up to 12 cards, each with image, name and description.',
        addCard: 'Add Card',
        namePlaceholder: 'Service name',
        descPlaceholder: 'Brief description...',
        uploadImage: 'Upload image',
        changeImage: 'Change image',
        uploading: 'Uploading...',
        uploadNotImage: 'Please select an image file',
        uploadTooLarge: 'Image size cannot exceed 5MB',
        uploadFail: 'Image upload failed',
        maxCards: 'You can add up to {max} cards'
    },
    leads: {
        title: 'Lead Capture',
        enableLabel: 'Enable lead capture form',
        enableDesc: 'When enabled, the portal will show a lead collection form',
        formFieldsLabel: 'Form fields preview',
        hint: 'Submitted info goes to Marketing Leads for follow-up.',
        fields: {
            companyName: 'Company Name',
            contactPerson: 'Contact Person',
            email: 'Email',
            phone: 'Phone',
            remark: 'Remarks'
        }
    },
    footer: {
        title: 'Footer Configuration',
        description: 'Configure footer Logo, slogan, legal info and social media',
        footerLogoLabel: 'Footer Logo',
        footerSloganLabel: 'Footer Slogan',
        footerSloganPlaceholder: 'Enter footer slogan',
        uploadLogo: 'Click to upload Logo',
        replaceLogo: 'Replace',
        cropLogo: 'Crop',
        removeLogo: 'Delete',
        logoSizeHint: 'Recommended ratio: 10:3',
        cropTitle: 'Crop Footer Logo',
        cropAreaAria: 'Adjustable crop area',
        cropSize: 'Current crop size: {w} × {h} px',
        cropRatioHint: 'Recommended ratio: 10:3',
        cropTip: 'Drag the corners to adjust the crop area. Only the cropped result will be saved.',
        cropSuccess: 'Crop result saved',
        legalInfo: 'Legal Information',
        copyrightLabel: 'Copyright',
        copyrightPlaceholder: 'e.g. © 2024 Your Company. All rights reserved.',
        privacyPolicyLabel: 'Privacy Policy URL',
        privacyPolicyPlaceholder: 'https://example.com/privacy',
        termsOfServiceLabel: 'Terms of Service URL',
        termsOfServicePlaceholder: 'https://example.com/terms',
        socialMedia: 'Social Media',
        filingInfo: 'Registration & Filing'
    },
    website: {
        description: 'Configure basic website information and display content',
        skinSettings: {
            title: 'Website Skin Settings',
            templateLabel: 'Website Template',
            templateTooltip: 'Choose the overall visual style of the website',
            preview: 'Preview',
            previewSuffix: 'style preview',
            themes: {
                business: 'Simple Business',
                premium: 'Premium Luxury',
                fresh: 'Fresh Modern',
                tech: 'Future Tech',
                custom: 'Custom'
            },
            primaryColor: 'Brand Primary',
            primaryColorHint: 'Primary buttons, links, selected states and key titles',
            accentColor: 'Accent Color',
            accentColorHint: 'Tags, secondary highlights, decorations and gradient ends'
        },
        basicInfo: {
            title: 'Basic Information Settings',
            domain: {
                label: 'Custom Domain Settings',
                placeholder: 'Please enter domain prefix',
                suffix: '.walltechsystem.com',
                edit: 'Edit',
                validate: 'Check Availability',
                cancel: 'Cancel',
                tip: 'Control Tower system supports using your own domain. If needed, please contact customer support.',
                warning: {
                    title: 'Domain Modification Reminder',
                    message: 'Domain can only be modified once every 6 months, please operate carefully',
                    note: 'Changes will take effect immediately, please ensure the new domain meets your needs',
                    confirm: 'Are you sure you want to modify the domain?',
                    okText: 'Confirm Modification'
                },
                check: {
                    title: 'Domain Availability Check',
                    available: {
                        title: '🎉 Congratulations, domain is available!',
                        note: 'Click "Confirm Modification" to complete domain setting'
                    },
                    unavailable: {
                        title: '😞 Sorry, this domain is already taken',
                        note: 'Please try another domain prefix'
                    }
                }
            },
            siteName: {
                label: 'Website Name',
                placeholder: 'Please enter website name'
            },
            headerLogo: {
                label: 'Header Logo Settings',
                logoOnly: 'Logo Only',
                logoWithSlogan: 'Logo + Slogan',
                upload: 'Upload Header Logo',
                sloganPlaceholder: 'Enter logo slogan (visible when Logo+Slogan is selected)',
                sloganDisabledHint: 'Select "Logo + Slogan" to edit slogan',
                sloganTip: 'Slogan will be displayed below the logo, recommend 1-2 lines'
            },
            footerLogo: {
                label: 'Footer Logo Settings',
                logoOnly: 'Logo Only',
                logoWithSlogan: 'Logo + Slogan',
                upload: 'Upload Footer Logo',
                sloganPlaceholder: 'Enter logo slogan (visible when Logo+Slogan is selected)',
                sloganDisabledHint: 'Select "Logo + Slogan" to edit slogan',
                sloganTip: 'Slogan will be displayed below the logo, recommend 1-2 lines'
            },
            copyright: {
                label: 'Copyright Information',
                placeholder: 'Please enter copyright information'
            },
            privacyPolicy: {
                label: 'Privacy Policy Link',
                placeholder: 'Please enter privacy policy page link'
            },
            termsOfService: {
                label: 'Terms of Service Link',
                placeholder: 'Please enter terms of service page link'
            }
        },
        socialMedia: {
            title: 'Social Media Settings',
            platforms: {
                wechat: 'WeChat',
                weibo: 'Weibo',
                linkedin: 'LinkedIn',
                twitter: 'Twitter',
                facebook: 'Facebook',
                youtube: 'YouTube',
                instagram: 'Instagram',
                tiktok: 'TikTok'
            },
            types: {
                qr: 'QR Code',
                link: 'Redirect Link'
            },
            uploadQr: 'Upload QR Code',
            linkPlaceholder: 'Please enter redirect link',
            linkInputPlaceholder: 'Enter {name} link'
        },
        filing: {
            title: 'Filing Information Settings',
            miit: {
                label: 'ICP Filing Number',
                placeholder: 'e.g. 京ICP备12345678号'
            },
            miitDate: {
                label: 'ICP Filing Date'
            },
            police: {
                label: 'Public Security Filing Number',
                placeholder: 'e.g. 京公网安备11010102001234号'
            },
            policeDate: {
                label: 'Public Security Filing Date'
            }
        },
        contact: {
            title: 'Contact Messages',
            sales: {
                title: 'Sales Contact',
                email: {
                    label: 'Sales Email',
                    placeholder: 'Please enter sales email'
                },
                phone: {
                    label: 'Sales Phone',
                    placeholder: 'Please enter sales phone'
                }
            },
            service: {
                title: 'Customer Service Contact',
                email: {
                    label: 'Customer Service Email',
                    placeholder: 'Please enter customer service email'
                },
                phone: {
                    label: 'Customer Service Phone',
                    placeholder: 'Please enter customer service phone'
                }
            },
            afterSales: {
                title: 'After-sales Contact',
                email: {
                    label: 'After-sales Email',
                    placeholder: 'Please enter after-sales email'
                },
                phone: {
                    label: 'After-sales Phone',
                    placeholder: 'Please enter after-sales phone'
                }
            }
        }
    },
    fields: {
        title: 'Page Field Configuration',
        developing: 'Page field configuration feature is under development...'
    },
    ai: {
        title: 'AI Assistant Configuration',
        developing: 'AI assistant configuration feature is under development...'
    },
    actions: {
        save: 'Save Configuration',
        update: 'Update Configuration',
        reset: 'Reset',
        confirm: 'Confirm',
        cancel: 'Cancel',
        close: 'Close',
        confirmModify: 'Confirm Modification'
    },
    messages: {
        saveSuccess: 'Save Successful',
        updateSuccess: 'Update Successful',
        switchSaveSuccess: 'Saved automatically',
        switchSaveNeedConfig: 'Please save portal configuration first before auto-save',
        resetSuccess: 'Configuration has been reset to default values',
        domainModifySuccess: 'Domain modified successfully!',
        domainValidationError:
            'Domain prefix must be at least 4 characters, containing only letters and numbers',
        cancelChanges: 'Changes cancelled',
        loadFail: 'Failed to load configuration',
        saveFail: 'Failed to save configuration',
        updateFail: 'Failed to update configuration',
        resetFail: 'Failed to reset configuration',
        logoPreviewSuccess: 'Logo preview loaded',
        logoPreviewFail: 'Failed to create logo preview',
        logoFileSelectFail: 'Failed to select logo file',
        qrPreviewFail: 'Failed to create QR code preview',
        defaultSiteName: 'Logistics Control Tower'
    },
    browserTab: {
        title: 'Browser Tab Settings',
        description: 'Customize the browser tab title and icon',
        pageTitleLabel: 'Tab Title',
        pageTitlePlaceholder: 'Please enter tab title',
        faviconLabel: 'Website Icon',
        faviconHint: '(Small icon shown on the browser tab)',
        uploadFavicon: 'Click to upload',
        uploadSizeHint: 'Recommended ratio: 1:1',
        replaceFavicon: 'Replace',
        cropFavicon: 'Crop',
        removeFavicon: 'Remove',
        cropTitle: 'Crop Website Icon',
        cropAreaAria: 'Adjustable crop area',
        cropSize: 'Current crop size: {w} × {h} px',
        cropRatioHint: 'Recommended ratio: 1:1',
        cropTip: 'Drag the corners of the crop box to adjust. Only the cropped result will be saved.',
        cropSuccess: 'Crop result saved'
    },
    domain: {
        title: 'Domain Settings',
        freeAlert:
            'You are on the public domain plan. The domain is assigned by the system and cannot be customized. Upgrade to unlock subdomain or custom domain.',
        portalDomainLabel: 'Portal Domain',
        /** Standard plan domainType=1 */
        subdomainLabel: 'Subdomain',
        /** Flagship plan domainType=2 */
        customDomainLabel: 'Custom Domain',
        subdomainPlaceholder: 'your-company',
        placeholder: 'portal.example.com',
        subdomainTip: 'Letters, numbers and hyphens only, length 2-63',
        flagshipTip:
            'Full domain names are supported (e.g. portal.example.com). Letters, numbers, hyphens and dots only.',
        assignedLabel: 'System-assigned Domain',
        assignedFallback: 'Portal address not assigned yet',
        upgradeHint: 'To use a custom domain, contact platform ops to upgrade the domain plan.',
        verify: 'Verify Domain',
        verifyAvailability: 'Check Availability',
        verifySuccess: 'Domain verified (available and reachable)',
        verifyFail: 'Domain verification failed',
        copy: 'Copy',
        copySuccess: 'Domain copied to clipboard',
        copyFail: 'Copy failed, please copy manually',
        errors: {
            empty: 'Please enter a domain',
            invalidFull: 'Please enter a full domain, e.g. portal.example.com',
            invalidSubdomain: 'Enter a 2-63 character subdomain prefix using letters, numbers or hyphens'
        },
        guide: {
            aria: 'View CargoWare X domain user guide',
            button: 'User Guide',
            title: 'CargoWare X Domain User Guide',
            ok: 'Got it',
            intro:
                'Full custom domains are supported. Complete DNS setup at your registrar first, then return here to verify. The system checks whether the domain is already used by another tenant and whether it is publicly reachable.',
            step1Title: '1. Add DNS records',
            step1Body:
                'Sign in to your DNS provider, open DNS management, and add the record for your domain type so it points to the platform ingress address generated for this tenant.',
            dnsIngressLabel: 'Platform ingress (record value)',
            dnsIngressValue: 'Address generated for this tenant',
            dnsSubdomainLabel: 'Subdomain',
            dnsSubdomainValue: 'Type: CNAME; Host: your domain prefix',
            dnsWildcardLabel: 'Wildcard domain',
            dnsWildcardValue: 'Type: CNAME; Host: *',
            dnsRootLabel: 'Root domain',
            dnsRootValue: "Follow your provider’s supported method; host is usually {'@'}",
            copyDns: 'Copy DNS config',
            copyDnsHint: 'Paste the platform ingress address into the provider’s record value field.',
            copyDnsSuccess: 'DNS config copied',
            step2Title: '2. Verify on this page',
            step2Body:
                'After configuration and confirming the domain is reachable, return here and click “Check Availability”. The system checks: 1) whether the domain is used by another tenant; 2) whether it is publicly reachable.',
            step3Title: '3. Act on the result',
            step3Body:
                'If both checks pass, you can use the domain. If verification fails, confirm the domain resolves correctly and is publicly reachable, then retry on this page.',
            note: 'Complete DNS setup and confirm the domain is reachable before verifying on this page.'
        }
    }
}